import { useState } from 'react'
import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Card,
  CardContent,
  Grid,
  Chip,
  Alert,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Stack
} from '@mui/material'
import {
  ExpandMore,
  CheckCircle,
  People,
  Business,
  ConfirmationNumber,
  Settings,
  IntegrationInstructions,
  Assessment,
  Email
} from '@mui/icons-material'

interface HelpSection {
  title: string
  icon: React.ReactNode
  description: string
  topics: HelpTopic[]
}

interface HelpTopic {
  question: string
  answer: string | React.ReactNode
}

const helpSections: HelpSection[] = [
  {
    title: 'Getting Started',
    icon: <Settings />,
    description: 'Learn the basics of managing your helpdesk system',
    topics: [
      {
        question: 'Understanding Your System Structure',
        answer: (
          <Box>
            <Typography paragraph>
              Your helpdesk uses a three-tier hierarchy designed for managing multiple customer locations:
            </Typography>
            <List dense>
              <ListItem>
                <ListItemIcon><Business fontSize="small" /></ListItemIcon>
                <ListItemText
                  primary="Your Organisation (Tenant)"
                  secondary="This is your company. You have full control and visibility across everything below."
                />
              </ListItem>
              <ListItem>
                <ListItemIcon><Business fontSize="small" /></ListItemIcon>
                <ListItemText
                  primary="Customer Organisations"
                  secondary="Your clients. For example: 'City of London Libraries' or 'ABC Retail Chain'"
                />
              </ListItem>
              <ListItem>
                <ListItemIcon><Business fontSize="small" /></ListItemIcon>
                <ListItemText
                  primary="Child Sites"
                  secondary="Individual locations within a customer. For example: 'Central Library', 'East Branch', etc."
                />
              </ListItem>
            </List>
            <Alert severity="info" sx={{ mt: 2 }}>
              <strong>Example:</strong> If you support "City Libraries", they might have child sites like "Barbican Library", "Central Library", and "Moorgate Library". Each site can have its own users and tickets, but you see everything from your admin dashboard.
            </Alert>
          </Box>
        )
      },
      {
        question: 'User Roles Explained',
        answer: (
          <Box>
            <List dense>
              <ListItem>
                <ListItemIcon><CheckCircle color="primary" /></ListItemIcon>
                <ListItemText
                  primary="Admin (You)"
                  secondary="Full system access. Manage all customers, sites, users, settings, and billing. See all tickets across all organisations."
                />
              </ListItem>
              <ListItem>
                <ListItemIcon><CheckCircle color="primary" /></ListItemIcon>
                <ListItemText
                  primary="Agent"
                  secondary="Your support staff. Can view, assign, and respond to tickets. Cannot access admin settings or billing."
                />
              </ListItem>
              <ListItem>
                <ListItemIcon><CheckCircle color="primary" /></ListItemIcon>
                <ListItemText
                  primary="Oversight"
                  secondary="Customer managers who need visibility across multiple child sites but cannot access your other customers' data."
                />
              </ListItem>
              <ListItem>
                <ListItemIcon><CheckCircle color="primary" /></ListItemIcon>
                <ListItemText
                  primary="Customer"
                  secondary="End users at specific sites. Can only create and view tickets for their own site."
                />
              </ListItem>
            </List>
          </Box>
        )
      },
      {
        question: 'First-Time Setup Checklist',
        answer: (
          <List dense>
            <ListItem>
              <ListItemText primary="1. Complete your organisation profile (Settings → General)" />
            </ListItem>
            <ListItem>
              <ListItemText primary="2. Create your first customer organisation (Organisations → Add Organisation)" />
            </ListItem>
            <ListItem>
              <ListItemText primary="3. Add child sites for that customer (if needed)" />
            </ListItem>
            <ListItem>
              <ListItemText primary="4. Create ticket categories (Settings → Categories)" />
            </ListItem>
            <ListItem>
              <ListItemText primary="5. Invite your agents (Users → Invite User → Select 'Agent' role)" />
            </ListItem>
            <ListItem>
              <ListItemText primary="6. Configure email ticketing (Settings → Email Integration)" />
            </ListItem>
            <ListItem>
              <ListItemText primary="7. Set up Microsoft Teams notifications (Settings → Integrations)" />
            </ListItem>
          </List>
        )
      }
    ]
  },
  {
    title: 'Managing Customers & Sites',
    icon: <Business />,
    description: 'How to set up and manage customer organisations and child sites',
    topics: [
      {
        question: 'Creating a New Customer Organisation',
        answer: (
          <Box>
            <Typography paragraph>
              Go to <strong>Organisations → Add Organisation</strong>
            </Typography>
            <List dense>
              <ListItem>
                <ListItemText primary="1. Enter the customer's company name (e.g., 'City of London Libraries')" />
              </ListItem>
              <ListItem>
                <ListItemText primary="2. Add a description (helps your team identify the customer)" />
              </ListItem>
              <ListItem>
                <ListItemText primary="3. Select organisation type (Library, Department, Branch, etc.)" />
              </ListItem>
              <ListItem>
                <ListItemText primary="4. Add contact details (email, phone, address)" />
              </ListItem>
              <ListItem>
                <ListItemText primary="5. Click 'Create Organisation'" />
              </ListItem>
            </List>
            <Alert severity="success" sx={{ mt: 2 }}>
              Once created, you can add child sites and invite users for this customer.
            </Alert>
          </Box>
        )
      },
      {
        question: 'Adding Child Sites',
        answer: (
          <Box>
            <Typography paragraph>
              Child sites are individual locations within a customer organisation.
            </Typography>
            <Typography paragraph>
              Go to <strong>Organisations → [Select Organisation] → Child Sites → Add Site</strong>
            </Typography>
            <List dense>
              <ListItem>
                <ListItemText primary="1. Enter site name (e.g., 'Barbican Library')" />
              </ListItem>
              <ListItem>
                <ListItemText primary="2. Add description and contact details" />
              </ListItem>
              <ListItem>
                <ListItemText primary="3. Select site type" />
              </ListItem>
              <ListItem>
                <ListItemText primary="4. Configure ticket visibility (who can see what)" />
              </ListItem>
            </List>
            <Alert severity="info" sx={{ mt: 2 }}>
              <strong>Pro Tip:</strong> Users assigned to a child site can only see tickets from their site. Oversight users can see tickets across multiple sites you assign to them.
            </Alert>
          </Box>
        )
      },
      {
        question: 'Managing Customer Users',
        answer: (
          <Box>
            <Typography paragraph>
              Go to <strong>Users → Invite User</strong>
            </Typography>
            <Typography paragraph><strong>To invite a customer user:</strong></Typography>
            <List dense>
              <ListItem>
                <ListItemText primary="1. Enter their email address" />
              </ListItem>
              <ListItem>
                <ListItemText primary="2. Enter their name" />
              </ListItem>
              <ListItem>
                <ListItemText primary="3. Select role: 'Customer' or 'Oversight'" />
              </ListItem>
              <ListItem>
                <ListItemText primary="4. Select their organisation and child site" />
              </ListItem>
              <ListItem>
                <ListItemText primary="5. Click 'Send Invitation'" />
              </ListItem>
            </List>
            <Typography paragraph sx={{ mt: 2 }}>
              They'll receive an email with a link to activate their account and set their password.
            </Typography>
          </Box>
        )
      }
    ]
  },
  {
    title: 'Managing Tickets',
    icon: <ConfirmationNumber />,
    description: 'Everything you need to know about handling support tickets',
    topics: [
      {
        question: 'How Tickets Are Created',
        answer: (
          <Box>
            <Typography paragraph>Tickets can be created in three ways:</Typography>
            <List dense>
              <ListItem>
                <ListItemIcon><Email fontSize="small" /></ListItemIcon>
                <ListItemText
                  primary="Email"
                  secondary="Customers send emails to your configured mailbox. Automatically creates tickets."
                />
              </ListItem>
              <ListItem>
                <ListItemIcon><Business fontSize="small" /></ListItemIcon>
                <ListItemText
                  primary="Customer Portal"
                  secondary="Customers log in and create tickets via the web portal."
                />
              </ListItem>
              <ListItem>
                <ListItemIcon><People fontSize="small" /></ListItemIcon>
                <ListItemText
                  primary="Agent/Admin"
                  secondary="You or your agents can create tickets on behalf of customers."
                />
              </ListItem>
            </List>
          </Box>
        )
      },
      {
        question: 'Assigning Tickets',
        answer: (
          <Box>
            <Typography paragraph>
              Open any ticket and click <strong>Assign To</strong> dropdown. Select an agent from your team.
            </Typography>
            <Alert severity="info">
              The assigned agent will receive a notification (if Teams integration is enabled). They'll see the ticket in their "My Assigned Tickets" view.
            </Alert>
            <Typography paragraph sx={{ mt: 2 }}>
              <strong>Best Practice:</strong> Assign tickets as soon as you know who should handle them. This prevents tickets from being forgotten.
            </Typography>
          </Box>
        )
      },
      {
        question: 'Ticket Categories',
        answer: (
          <Box>
            <Typography paragraph>
              Categories help organise tickets by type (e.g., "Hardware", "Software", "Network").
            </Typography>
            <Typography paragraph>
              <strong>To create categories:</strong> Settings → Categories → Add Category
            </Typography>
            <Typography paragraph>
              <strong>Global vs Site-Specific:</strong>
            </Typography>
            <List dense>
              <ListItem>
                <ListItemText
                  primary="Global categories"
                  secondary="Available to all child sites. Good for common issues like 'Password Reset' or 'Hardware Problem'."
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="Site-specific categories"
                  secondary="Only available to selected child sites. Good for location-specific issues."
                />
              </ListItem>
            </List>
          </Box>
        )
      },
      {
        question: 'Using Internal Notes',
        answer: (
          <Box>
            <Typography paragraph>
              Internal notes are visible only to your team (agents and admins). Customers cannot see them.
            </Typography>
            <Typography paragraph>
              <strong>Use internal notes for:</strong>
            </Typography>
            <List dense>
              <ListItem>
                <ListItemText primary="• Documenting troubleshooting steps" />
              </ListItem>
              <ListItem>
                <ListItemText primary="• Sharing information with other agents" />
              </ListItem>
              <ListItem>
                <ListItemText primary="• Recording phone conversations" />
              </ListItem>
              <ListItem>
                <ListItemText primary="• Noting customer history or context" />
              </ListItem>
            </List>
            <Alert severity="warning" sx={{ mt: 2 }}>
              <strong>Remember:</strong> Regular comments are visible to customers. Use internal notes for team-only information.
            </Alert>
          </Box>
        )
      },
      {
        question: 'Ticket Priority & SLA',
        answer: (
          <Box>
            <Typography paragraph>
              <strong>Priority Levels:</strong>
            </Typography>
            <List dense>
              <ListItem>
                <ListItemText
                  primary="Urgent"
                  secondary="System down, critical issue affecting multiple users"
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="High"
                  secondary="Important issue, user cannot complete work"
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="Medium"
                  secondary="Issue with workaround available"
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="Low"
                  secondary="Minor issue, feature request, question"
                />
              </ListItem>
            </List>
            <Typography paragraph sx={{ mt: 2 }}>
              SLA (Service Level Agreement) tracking shows if tickets are responded to and resolved within target times. Configure SLA targets in Settings → General.
            </Typography>
          </Box>
        )
      }
    ]
  },
  {
    title: 'Email Integration',
    icon: <Email />,
    description: 'Set up email-to-ticket conversion with Microsoft 365',
    topics: [
      {
        question: 'Configuring Email Ticketing',
        answer: (
          <Box>
            <Typography paragraph>
              Email ticketing allows customers to create tickets by sending emails to your support mailbox.
            </Typography>
            <Typography paragraph>
              <strong>Requirements:</strong>
            </Typography>
            <List dense>
              <ListItem>
                <ListItemText primary="• Microsoft 365 subscription" />
              </ListItem>
              <ListItem>
                <ListItemText primary="• A shared mailbox (e.g., support@yourcompany.com)" />
              </ListItem>
              <ListItem>
                <ListItemText primary="• Azure AD app registration" />
              </ListItem>
            </List>
            <Alert severity="info" sx={{ mt: 2 }}>
              Follow the Email Integration setup guide in Settings → Email Integration for step-by-step instructions.
            </Alert>
          </Box>
        )
      },
      {
        question: 'How Email Tickets Work',
        answer: (
          <Box>
            <Typography paragraph>
              <strong>When a customer sends an email:</strong>
            </Typography>
            <List dense>
              <ListItem>
                <ListItemText primary="1. System checks the mailbox every 2 minutes" />
              </ListItem>
              <ListItem>
                <ListItemText primary="2. Creates a new ticket from the email subject and body" />
              </ListItem>
              <ListItem>
                <ListItemText primary="3. Attaches any files from the email" />
              </ListItem>
              <ListItem>
                <ListItemText primary="4. Assigns to the customer's site based on sender email" />
              </ListItem>
              <ListItem>
                <ListItemText primary="5. Sends confirmation email to customer" />
              </ListItem>
            </List>
            <Typography paragraph sx={{ mt: 2 }}>
              <strong>When you reply:</strong> Your comments from the portal are sent as email replies to the customer.
            </Typography>
          </Box>
        )
      }
    ]
  },
  {
    title: 'Integrations',
    icon: <IntegrationInstructions />,
    description: 'Connect your helpdesk with Microsoft Teams and other tools',
    topics: [
      {
        question: 'Microsoft Teams Notifications',
        answer: (
          <Box>
            <Typography paragraph>
              Get real-time notifications in Microsoft Teams when:
            </Typography>
            <List dense>
              <ListItem>
                <ListItemText primary="• New tickets are created" />
              </ListItem>
              <ListItem>
                <ListItemText primary="• Tickets are assigned to agents" />
              </ListItem>
              <ListItem>
                <ListItemText primary="• Ticket status changes" />
              </ListItem>
              <ListItem>
                <ListItemText primary="• Tickets are resolved" />
              </ListItem>
            </List>
            <Typography paragraph sx={{ mt: 2 }}>
              <strong>Setup:</strong> Go to Settings → Integrations → Microsoft Teams and follow the setup guide.
            </Typography>
            <Alert severity="success">
              Notifications go to a single Teams channel and include ALL tickets across ALL your customer sites.
            </Alert>
          </Box>
        )
      },
      {
        question: 'Zapier Integration',
        answer: (
          <Box>
            <Typography paragraph>
              Connect your helpdesk to 5,000+ apps using Zapier webhooks.
            </Typography>
            <Typography paragraph>
              <strong>Popular use cases:</strong>
            </Typography>
            <List dense>
              <ListItem>
                <ListItemText primary="• Add tickets to Google Sheets for reporting" />
              </ListItem>
              <ListItem>
                <ListItemText primary="• Send SMS alerts for urgent tickets" />
              </ListItem>
              <ListItem>
                <ListItemText primary="• Create tasks in project management tools" />
              </ListItem>
              <ListItem>
                <ListItemText primary="• Log tickets in your CRM system" />
              </ListItem>
            </List>
            <Typography paragraph sx={{ mt: 2 }}>
              Setup guide available at: Settings → Integrations → Zapier
            </Typography>
          </Box>
        )
      }
    ]
  },
  {
    title: 'Reporting & Analytics',
    icon: <Assessment />,
    description: 'Understanding your support performance metrics',
    topics: [
      {
        question: 'Available Reports',
        answer: (
          <Box>
            <Typography paragraph>
              Access reports from the <strong>Analytics</strong> section:
            </Typography>
            <List dense>
              <ListItem>
                <ListItemText
                  primary="Ticket Volume"
                  secondary="Track tickets created, resolved, and closed over time"
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="Response Times"
                  secondary="Average time to first response and resolution"
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="SLA Compliance"
                  secondary="Percentage of tickets meeting SLA targets"
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="Agent Performance"
                  secondary="Tickets handled per agent, resolution rates"
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="Customer Breakdown"
                  secondary="Tickets by organisation and child site"
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="Category Analysis"
                  secondary="Most common ticket types"
                />
              </ListItem>
            </List>
          </Box>
        )
      },
      {
        question: 'Filtering Reports',
        answer: (
          <Box>
            <Typography paragraph>
              All reports can be filtered by:
            </Typography>
            <List dense>
              <ListItem>
                <ListItemText primary="• Date range (last 7 days, 30 days, custom range)" />
              </ListItem>
              <ListItem>
                <ListItemText primary="• Customer organisation" />
              </ListItem>
              <ListItem>
                <ListItemText primary="• Child site" />
              </ListItem>
              <ListItem>
                <ListItemText primary="• Priority level" />
              </ListItem>
              <ListItem>
                <ListItemText primary="• Category" />
              </ListItem>
              <ListItem>
                <ListItemText primary="• Assigned agent" />
              </ListItem>
            </List>
            <Alert severity="info" sx={{ mt: 2 }}>
              Use filters to drill down into specific customers, time periods, or agents to identify trends and areas for improvement.
            </Alert>
          </Box>
        )
      }
    ]
  },
  {
    title: 'Billing & Subscription',
    icon: <Settings />,
    description: 'Managing your subscription and billing',
    topics: [
      {
        question: 'Understanding Your Bill',
        answer: (
          <Box>
            <Typography paragraph>
              Your subscription is based on the number of <strong>agent seats</strong> you need.
            </Typography>
            <Typography paragraph>
              <strong>£25 per agent per month</strong>
            </Typography>
            <List dense>
              <ListItem>
                <ListItemText
                  primary="Agents"
                  secondary="Your support team members who respond to tickets"
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="Not counted"
                  secondary="Customer users, oversight users - unlimited at no extra cost"
                />
              </ListItem>
            </List>
            <Alert severity="success" sx={{ mt: 2 }}>
              No limits on customers, sites, tickets, or storage. Just pay for your support team size.
            </Alert>
          </Box>
        )
      },
      {
        question: 'Upgrading or Downgrading',
        answer: (
          <Box>
            <Typography paragraph>
              Go to <strong>Settings → Billing & Subscription → Upgrade Plan</strong>
            </Typography>
            <List dense>
              <ListItem>
                <ListItemText primary="1. Adjust the agent count slider" />
              </ListItem>
              <ListItem>
                <ListItemText primary="2. See your new monthly total" />
              </ListItem>
              <ListItem>
                <ListItemText primary="3. Update payment method if needed" />
              </ListItem>
              <ListItem>
                <ListItemText primary="4. Confirm changes" />
              </ListItem>
            </List>
            <Typography paragraph sx={{ mt: 2 }}>
              Changes take effect immediately. Billing is prorated - you only pay for what you use.
            </Typography>
          </Box>
        )
      }
    ]
  }
]

function AdminHelpGuide() {
  const [expanded, setExpanded] = useState<string | false>(false)
  const [selectedSection, setSelectedSection] = useState<number>(0)

  const handleAccordionChange = (panel: string) => (_event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpanded(isExpanded ? panel : false)
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold', mb: 3 }}>
        Admin & Agent Help Guide
      </Typography>

      <Grid container spacing={3}>
        {/* Section Navigation */}
        <Grid item xs={12} md={3}>
          <Card sx={{ position: 'sticky', top: 20 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Topics
              </Typography>
              <Stack spacing={1}>
                {helpSections.map((section, index) => (
                  <Chip
                    key={index}
                    icon={section.icon as React.ReactElement}
                    label={section.title}
                    onClick={() => setSelectedSection(index)}
                    color={selectedSection === index ? 'primary' : 'default'}
                    variant={selectedSection === index ? 'filled' : 'outlined'}
                    sx={{ justifyContent: 'flex-start', px: 1 }}
                  />
                ))}
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* Content Area */}
        <Grid item xs={12} md={9}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                {helpSections[selectedSection].icon}
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                    {helpSections[selectedSection].title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {helpSections[selectedSection].description}
                  </Typography>
                </Box>
              </Box>

              <Divider sx={{ my: 3 }} />

              <Stack spacing={2}>
                {helpSections[selectedSection].topics.map((topic, topicIndex) => (
                  <Accordion
                    key={topicIndex}
                    expanded={expanded === `panel${topicIndex}`}
                    onChange={handleAccordionChange(`panel${topicIndex}`)}
                  >
                    <AccordionSummary expandIcon={<ExpandMore />}>
                      <Typography sx={{ fontWeight: 600 }}>{topic.question}</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      {typeof topic.answer === 'string' ? (
                        <Typography color="text.secondary">{topic.answer}</Typography>
                      ) : (
                        topic.answer
                      )}
                    </AccordionDetails>
                  </Accordion>
                ))}
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  )
}

export default AdminHelpGuide
